import { diffObject } from '@/helpers/resourceful/compare'
import { contractTypes, pricingMethods } from '../fields/contract-detail-fields'
import { omitBy, isUndefined } from 'lodash'
import { GET_CITIES, GET_PROVINCES } from '@/services/api/master/address'

/**
 * Remove vendor_id from payload
 * @param {object} payload
 * @returns {object} transformed payload
 */
export const transformContractStoreRequest = (payload) => {
  const { vendor_id, ...rest } = payload
  return {
    ...rest,
    payment_term: Number(rest.payment_term),
  }
}

export const transformContractUpdateRequest = (newData, oldData) => {
  const [diff, _] = diffObject(newData, oldData)
  return {
    ...diff,
    payment_term: Number(diff.payment_term),
  }
}

export const transformContractShowResponse = (data) => {
  const transformed = {
    vendor_id: data.vendor.id,
    custodian_id: data.custodian?.id,

    contract_type: contractTypes.find((type) => type.id === data.contract_type)?.id,
    pricing_method: pricingMethods.find((method) => method.id === data.pricing_method)?.id,

    reference: data.reference,
    effective_date: data.effective_date,
    expired_date: data.expired_date,
    payment_term: data.payment_term,
    note: data.note,
  }

  return transformed
}

export const transformRatesShowResponse = (data) => {
  const addressTypeMappings = {
    Kota: 'CITY',
    Provinsi: 'PROVINCE',
  }

  const transformed = {
    box_type_id: data.box_type.id,
    origin_type: addressTypeMappings[data.origin.type],
    origin_name: data.origin.name,
    destination_type: addressTypeMappings[data.destination.type],
    destination_name: data.destination.name,
    base_price: data.base_price,
  }

  return transformed
}

export const transformRatesStoreRequest = (payload) => {
  const additionalCosts = payload.additional_costs.map((cost) => {
    return {
      id: cost.additional_cost.id,
      price: Number(cost.price),
    }
  })

  return {
    box_id: payload.box_type_id,
    origin: {
      type: payload.origin_type,
      name: payload.origin_name,
    },
    destination: {
      type: payload.destination_type,
      name: payload.destination_name,
    },
    base_price: Number(payload.base_price),
    additional_costs: additionalCosts,
  }
}

export const transformRatesUpdateRequest = (newData, oldData) => {
  const [diff, _] = diffObject(newData, oldData)
  let transformedDiff = {}

  if ('base_price' in diff) {
    transformedDiff.base_price = Number(diff.base_price)
  }

  if ('box_type_id' in diff) {
    transformedDiff.box_id = diff.box_type_id
  }

  if (diff.origin_type || diff.origin_name) {
    transformedDiff.origin = {
      type: diff.origin_type,
      name: diff.origin_name,
    }

    transformedDiff.origin = omitBy(transformedDiff.origin, isUndefined)
  }

  if (diff.destination_type || diff.destination_name) {
    transformedDiff.destination = {
      type: diff.destination_type,
      name: diff.destination_name,
    }

    transformedDiff.destination = omitBy(transformedDiff.destination, isUndefined)
  }

  return transformedDiff
}

async function fetchCities(opts) {
  const citiesResponse = await this.$apollo.query({
    query: GET_CITIES,
    fetchPolicy: 'no-cache',
    variables: {
      filter: {
        pagination: {
          page: opts?.page,
          show: opts?.limit || 5,
        },
        search: opts?.search || undefined,
      },
    },
  })

  const citiesData = citiesResponse.data.data.cities.data

  return {
    metadata: {
      total_count: citiesResponse.data.data.cities.pagination.total,
      page: citiesResponse.data.data.cities.pagination.page,
    },
    data: {
      paginated_result: citiesData,
    },
  }
}

async function fetchProvinces(opts) {
  const provincesResponse = await this.$apollo.query({
    query: GET_PROVINCES,
    fetchPolicy: 'no-cache',
    variables: {
      filter: {
        pagination: {
          page: opts?.page,
          show: opts?.limit || 5,
        },
        search: opts?.search || undefined,
      },
    },
  })

  const provincesData = provincesResponse.data.data.provinces.data

  return {
    metadata: {
      total_count: provincesResponse.data.data.provinces.pagination.total,
      page: provincesResponse.data.data.provinces.pagination.page,
    },
    data: {
      paginated_result: provincesData,
    },
  }
}

export const transformAddressGroupByType = (formValues, addressGroups) => {
  const optionsGetters = {
    CITY: fetchCities,
    PROVINCE: fetchProvinces,
  }

  return addressGroups.map((group) => {
    const addressType = formValues[`${group.name}_type`]

    return {
      ...group,
      fields: group.fields.map((field) => {
        if (field.name === 'name') {
          return {
            ...field,
            disabled: !addressType,
            optionsGetter: optionsGetters[addressType],
          }
        }

        return field
      }),
    }
  })
}

export const transformAdditionalCostStoreRequest = (payload) => {
  return {
    id: payload.additional_cost_id,
    price: Number(payload.price),
  }
}

export const transformAdditionalCostEmitPayload = (payload, selectedAdditionalCost, id) => {
  return {
    id,
    additional_cost: {
      id: selectedAdditionalCost.id,
      name: selectedAdditionalCost.name,
    },
    price: payload.price,
  }
}

export const transformAdditionalCostUpdateRequest = (newData, oldData) => {
  // From form
  const formValues = {
    id: newData.additional_cost_id,
    price: Number(newData.price),
  }

  // From show/list api
  const oldFormValues = {
    id: oldData.additional_cost.id,
    price: Number(oldData.price),
  }

  const [diff, _] = diffObject(formValues, oldFormValues)
  return diff
}
